import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { ChevronDown, Search, Users } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

/**
 * Extrahiert den aktuell getippten Empfänger (letztes Segment nach Komma).
 */
function splitRecipientDraft(value = '') {
  const raw = String(value || '');
  const lastComma = raw.lastIndexOf(',');
  if (lastComma === -1) {
    return { prefix: '', draft: raw };
  }
  return {
    prefix: raw.slice(0, lastComma + 1),
    draft: raw.slice(lastComma + 1).replace(/^\s+/, ''),
  };
}

function applySuggestion(value, email) {
  const { prefix } = splitRecipientDraft(value);
  const next = prefix ? `${prefix} ${email}` : email;
  return next;
}

function contactLabel(c) {
  return String(c?.name || '').trim() || String(c?.email || '').trim() || 'Kontakt';
}

const RecipientSuggestInput = ({
  value,
  onChange,
  className = '',
  placeholder = '',
  autoFocus = false,
  required = false,
  id,
  inputClassName,
  showContactPicker = true,
  ...inputProps
}) => {
  const { isDarkMode } = useTheme();
  const listId = useId();
  const pickerListId = useId();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const pickerBtnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [pickerFilter, setPickerFilter] = useState('');
  const [pickerStyle, setPickerStyle] = useState(null);
  const [pickerHighlight, setPickerHighlight] = useState(0);
  const pickerSearchRef = useRef(null);
  const contactsLoadedRef = useRef(false);

  const { draft } = splitRecipientDraft(value);
  const query = draft.trim();

  useEffect(() => {
    if (!open || !query || pickerOpen) {
      if (!query) {
        setSuggestions([]);
        setLoading(false);
      }
      return undefined;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const reqId = ++requestIdRef.current;
      setLoading(true);
      try {
        const res = await axios.get('/api/recipient-suggestions', {
          params: { q: query, limit: 8 },
        });
        if (reqId !== requestIdRef.current) return;
        const list = Array.isArray(res.data?.suggestions) ? res.data.suggestions : [];
        const filtered = list.filter(
          (item) => String(item.email || '').toLowerCase() !== query.toLowerCase()
        );
        setSuggestions(filtered);
        setHighlight(0);
      } catch (_) {
        if (reqId === requestIdRef.current) setSuggestions([]);
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    }, 180);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, pickerOpen]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      const inWrap = wrapRef.current?.contains(e.target);
      const inSuggest = e.target.closest?.('[data-recipient-suggest-menu]');
      const inPicker = e.target.closest?.('[data-recipient-picker-menu]');
      if (!inWrap && !inSuggest && !inPicker) {
        setOpen(false);
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const updateMenuPosition = () => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 220),
      zIndex: 200,
    });
  };

  const updatePickerPosition = () => {
    const el = pickerBtnRef.current || wrapRef.current || inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.min(Math.max(wrapRef.current?.getBoundingClientRect().width || rect.width, 280), 420);
    let left = rect.right - width;
    left = Math.min(Math.max(8, left), window.innerWidth - width - 8);
    setPickerStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left,
      width,
      zIndex: 210,
    });
  };

  useLayoutEffect(() => {
    if (!open || !query || pickerOpen) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, query, suggestions.length, loading, pickerOpen]);

  useLayoutEffect(() => {
    if (!pickerOpen) {
      setPickerStyle(null);
      return undefined;
    }
    updatePickerPosition();
    const onScrollOrResize = () => updatePickerPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [pickerOpen, contacts.length, pickerFilter]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const t = setTimeout(() => pickerSearchRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [pickerOpen]);

  const loadContacts = async ({ force = false } = {}) => {
    if (contactsLoadedRef.current && !force && contacts.length > 0) return;
    setContactsLoading(true);
    try {
      const res = await axios.get('/api/contacts');
      const list = Array.isArray(res.data) ? res.data : [];
      const withEmail = list
        .filter((c) => String(c?.email || '').trim())
        .map((c) => ({
          id: c.id,
          name: c.name || '',
          email: String(c.email).trim(),
          company: c.company || '',
        }))
        .sort((a, b) =>
          contactLabel(a).localeCompare(contactLabel(b), 'de', { sensitivity: 'base' })
        );
      setContacts(withEmail);
      contactsLoadedRef.current = true;
    } catch (_) {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const filteredContacts = useMemo(() => {
    const q = pickerFilter.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      const hay = `${c.name} ${c.email} ${c.company}`.toLowerCase();
      return hay.includes(q);
    });
  }, [contacts, pickerFilter]);

  useEffect(() => {
    setPickerHighlight(0);
  }, [pickerFilter, pickerOpen]);

  const pick = (item) => {
    const email = item?.email;
    if (!email) return;
    onChange(applySuggestion(value, email));
    setOpen(false);
    setPickerOpen(false);
    setSuggestions([]);
    setPickerFilter('');
    inputRef.current?.focus();
  };

  const togglePicker = async () => {
    if (pickerOpen) {
      setPickerOpen(false);
      return;
    }
    setOpen(false);
    setSuggestions([]);
    setPickerFilter('');
    setPickerOpen(true);
    await loadContacts();
  };

  const onKeyDown = (e) => {
    if (pickerOpen) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setPickerOpen(false);
        return;
      }
      if (e.key === 'ArrowDown' && filteredContacts.length) {
        e.preventDefault();
        setPickerHighlight((i) => (i + 1) % filteredContacts.length);
        return;
      }
      if (e.key === 'ArrowUp' && filteredContacts.length) {
        e.preventDefault();
        setPickerHighlight((i) => (i - 1 + filteredContacts.length) % filteredContacts.length);
        return;
      }
      if (e.key === 'Enter' && filteredContacts[pickerHighlight]) {
        e.preventDefault();
        pick(filteredContacts[pickerHighlight]);
        return;
      }
    }

    if (!open || suggestions.length === 0) {
      inputProps.onKeyDown?.(e);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => (i + 1) % suggestions.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => (i - 1 + suggestions.length) % suggestions.length);
      return;
    }
    if (e.key === 'Enter' && suggestions[highlight]) {
      e.preventDefault();
      pick(suggestions[highlight]);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    inputProps.onKeyDown?.(e);
  };

  const showList = open && !pickerOpen && !!query && (suggestions.length > 0 || loading);

  const menuClass = isDarkMode
    ? 'max-h-56 overflow-y-auto rounded-lg border border-neutral-700 bg-neutral-900 shadow-xl'
    : 'max-h-56 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg';

  const pickerMenuClass = isDarkMode
    ? 'rounded-lg border border-neutral-700 bg-neutral-900 shadow-xl overflow-hidden flex flex-col max-h-[min(22rem,70vh)]'
    : 'rounded-lg border border-neutral-200 bg-white shadow-lg overflow-hidden flex flex-col max-h-[min(22rem,70vh)]';

  const itemActiveClass = isDarkMode ? 'bg-primary-950/50' : 'bg-primary-50';
  const itemIdleClass = isDarkMode ? 'hover:bg-neutral-800' : 'hover:bg-neutral-50';
  const emailClass = isDarkMode
    ? 'block text-[12px] font-medium text-neutral-100 truncate'
    : 'block text-[12px] font-medium text-neutral-900 truncate';
  const nameClass = isDarkMode
    ? 'block text-[12px] font-medium text-neutral-100 truncate'
    : 'block text-[12px] font-medium text-neutral-900 truncate';
  const metaClass = isDarkMode
    ? 'block text-[10px] text-neutral-400 truncate'
    : 'block text-[10px] text-neutral-500 truncate';

  const mergedInputClass = inputClassName || '';

  return (
    <div ref={wrapRef} className={`flex items-stretch gap-1.5 min-w-0 ${className}`.trim()} id={id}>
      <div className="relative min-w-0 flex-1">
        <input
          {...inputProps}
          ref={inputRef}
          type="text"
          autoComplete="off"
          autoFocus={autoFocus}
          required={required}
          value={value}
          placeholder={placeholder}
          className={mergedInputClass}
          onChange={(e) => {
            const next = e.target.value;
            onChange(next);
            setPickerOpen(false);
            const nextDraft = splitRecipientDraft(next).draft.trim();
            setOpen(!!nextDraft);
            if (!nextDraft) {
              setSuggestions([]);
              setLoading(false);
            }
          }}
          onFocus={(e) => {
            if (query && !pickerOpen) setOpen(true);
            inputProps.onFocus?.(e);
          }}
          onKeyDown={onKeyDown}
          aria-autocomplete="list"
          aria-controls={pickerOpen ? pickerListId : listId}
        />
      </div>

      {showContactPicker ? (
        <button
          ref={pickerBtnRef}
          type="button"
          tabIndex={-1}
          title="Kontakt auswählen"
          aria-label="Kontakt auswählen"
          aria-expanded={pickerOpen}
          aria-controls={pickerListId}
          aria-haspopup="listbox"
          onMouseDown={(e) => e.preventDefault()}
          onClick={togglePicker}
          className={`flex-shrink-0 inline-flex items-center justify-center gap-1 px-2 rounded-lg border text-[11px] font-medium transition-colors ${
            pickerOpen
              ? 'border-primary-400 text-primary-600 bg-primary-50 dark:bg-primary-950/40 dark:text-primary-300 dark:border-primary-600'
              : 'border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Kontakte</span>
          <ChevronDown
            className={`w-3 h-3 transition-transform duration-200 ${pickerOpen ? 'rotate-180' : ''}`}
          />
        </button>
      ) : null}

      {showList &&
        menuStyle &&
        createPortal(
          <div
            id={listId}
            role="listbox"
            data-recipient-suggest-menu
            style={menuStyle}
            className={menuClass}
          >
            {loading && suggestions.length === 0 ? (
              <p className={`px-3 py-2 text-[11px] ${isDarkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>
                Laden…
              </p>
            ) : (
              suggestions.map((item, index) => {
                const active = index === highlight;
                const meta = item.name
                  ? item.name
                  : item.sendCount > 0
                    ? `${item.sendCount}× gesendet`
                    : 'Kontakt';
                return (
                  <button
                    key={item.email}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${
                      active ? itemActiveClass : itemIdleClass
                    }`}
                    onMouseEnter={() => setHighlight(index)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(item);
                    }}
                  >
                    <span className="min-w-0">
                      <span className={emailClass}>{item.email}</span>
                      <span className={metaClass}>{meta}</span>
                    </span>
                    {item.sendCount > 0 && item.name ? (
                      <span className={`text-[10px] flex-shrink-0 ${isDarkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>
                        {item.sendCount}×
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>,
          document.body
        )}

      {pickerOpen &&
        pickerStyle &&
        createPortal(
          <div
            id={pickerListId}
            role="listbox"
            data-recipient-picker-menu
            style={pickerStyle}
            className={pickerMenuClass}
          >
            <div className={`flex-shrink-0 px-2.5 pt-2.5 pb-2 border-b ${isDarkMode ? 'border-neutral-800' : 'border-neutral-100'}`}>
              <div className="flex items-center gap-1.5 mb-2 px-0.5">
                <Users className={`w-3.5 h-3.5 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`} />
                <p className={`text-[11px] font-medium ${isDarkMode ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  Kontakte
                  {contacts.length ? ` · ${contacts.length}` : ''}
                </p>
              </div>
              <div className="relative">
                <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDarkMode ? 'text-neutral-500' : 'text-neutral-400'}`} />
                <input
                  ref={pickerSearchRef}
                  type="text"
                  value={pickerFilter}
                  onChange={(e) => setPickerFilter(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Name oder E-Mail suchen…"
                  className={`w-full pl-8 pr-2.5 py-1.5 text-[12px] rounded-md border outline-none focus:ring-1 focus:ring-primary-500 ${
                    isDarkMode
                      ? 'border-neutral-700 bg-neutral-950 text-neutral-100 placeholder:text-neutral-500'
                      : 'border-neutral-200 bg-neutral-50 text-neutral-900 placeholder:text-neutral-400'
                  }`}
                />
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              {contactsLoading ? (
                <p className={`px-3 py-4 text-[11px] text-center ${isDarkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>
                  Kontakte werden geladen…
                </p>
              ) : filteredContacts.length === 0 ? (
                <p className={`px-3 py-4 text-[11px] text-center ${isDarkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>
                  {contacts.length === 0 ? 'Keine Kontakte vorhanden' : 'Keine Treffer'}
                </p>
              ) : (
                filteredContacts.map((item, index) => {
                  const active = index === pickerHighlight;
                  return (
                    <button
                      key={`${item.id || item.email}-${item.email}`}
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`w-full text-left px-3 py-2 ${active ? itemActiveClass : itemIdleClass}`}
                      onMouseEnter={() => setPickerHighlight(index)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pick(item);
                      }}
                    >
                      <span className={nameClass}>{contactLabel(item)}</span>
                      <span className={metaClass}>
                        {item.email}
                        {item.company ? ` · ${item.company}` : ''}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default RecipientSuggestInput;
